<!-- src/components/organization/BaseStaffConfigForm.vue -->
<!-- Base staff configuration form component - Reusable for onboarding and editing, one name per line -->
<!-- This is a "dumb" component with no API logic, only form fields and validation -->
<!-- RELEVANT FILES: nav-team-add-step4-staff.vue, nav-team-manage.vue, organization-config.ts -->

<script setup lang="ts">
import { toTypedSchema } from '@vee-validate/zod';
import { useForm } from 'vee-validate';
import { z } from 'zod';

import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Textarea } from '@/components/ui/textarea';

interface Props {
  initialData?: string;
  isSubmitting?: boolean;
  showSkip?: boolean;
  submitLabel?: string;
}

const props = withDefaults(defineProps<Props>(), {
  initialData: '',
  isSubmitting: false,
  showSkip: false,
  submitLabel: 'Save',
});

const emit = defineEmits<{
  submit: [data: string];
  cancel: [];
}>();

// Simple validation for staff names string
const staffSchema = z.object({
  staff: z.string().max(500, 'Staff names list is too long').optional().or(z.literal('')),
});

const formSchema = toTypedSchema(staffSchema);

const { handleSubmit, resetForm } = useForm({
  validationSchema: formSchema,
  initialValues: {
    staff: props.initialData,
  },
});

// Watch for prop changes and reset form
watch(
  () => props.initialData,
  newData => {
    nextTick(() => {
      resetForm({
        values: {
          staff: newData || '',
        },
      });
    });
  },
  { deep: true },
);

// Form submission
const onFormSubmit = handleSubmit(data => {
  emit('submit', data.staff || '');
});

// Reset form to initial values
function handleReset() {
  resetForm({
    values: {
      staff: props.initialData || '',
    },
  });
}
</script>

<template>
  <form class="space-y-4" @submit="onFormSubmit">
    <!-- Staff Names Input -->
    <FormField v-slot="{ componentField }" name="staff">
      <FormItem>
        <FormLabel class="text-sm font-medium"> Staff Names </FormLabel>
        <FormControl>
          <Textarea
            v-bind="componentField"
            placeholder="max&#10;peter&#10;sarah&#10;john"
            class="resize-none"
            rows="3"
          />
        </FormControl>
        <FormDescription class="text-xs">
          Enter staff member names, one per line. Press Enter after each name for call tracking and
          performance insights.
        </FormDescription>
        <FormMessage />
      </FormItem>
    </FormField>

    <!-- Action Buttons -->
    <div class="flex justify-end items-center gap-3 pt-2">
      <UiButton
        v-if="showSkip"
        type="button"
        variant="outline"
        :disabled="isSubmitting"
        @click="emit('cancel')"
      >
        Skip for Now
      </UiButton>
      <UiButton
        v-else
        type="button"
        variant="outline"
        :disabled="isSubmitting"
        @click="handleReset"
      >
        Reset
      </UiButton>
      <UiButton type="submit" :loading="isSubmitting" :disabled="isSubmitting">
        {{ submitLabel }}
      </UiButton>
    </div>
  </form>
</template>
